import { SURGERY_BASE_URL } from "@qendo/medical";
import { MedicalType } from "@qendo/medical/types";
import { OptionType } from "@qendo/types";
import type { DateArg } from "date-fns";
import { router } from "expo-router";
import { useEffect, useId, useState } from "react";

import { Button } from "~/components/Button";
import { OptionSelector } from "~/components/form/combo-box/OptionSelector";
import { DatePicker } from "~/components/form/date-picker/DatePicker";
import { FieldGroup } from "~/components/form/FieldGroup";
import { MediaPicker } from "~/components/form/gallery/MediaPicker";
import { TextArea } from "~/components/form/TextArea";
import { FullPageModal, type FullPageModalProps } from "~/components/layout/modal/FullPageModal";
import { Space } from "~/constants/sizing";
import type { Surgery } from "~/entities/medical/surgery";
import { useFeedback } from "~/providers/FeedbackProvider";
import { useMedical } from "~/providers/MedicalProvider";
import { elementsMatch } from "~/util/array";

type SurgeryFormProps = {
    readonly surgery?: Surgery;
    onRequestClose: () => void;
} & FullPageModalProps;

export const SurgeryForm = ({ surgery, onRequestClose, ...modalProps }: SurgeryFormProps) => {
    const { canUpdateMedical, storeMedical } = useMedical();
    const { saving, setSaving } = useFeedback();
    const [option, setOption] = useState<string | null>(surgery?.option_id ?? null);
    const [procedureDate, setProcedureDate] = useState<DateArg<Date> | null>(surgery?.procedure_date ?? null);
    const [description, setDescription] = useState(surgery?.description ?? "");
    const [photographs, setPhotographs] = useState<string[]>(surgery?.photograph_ids ?? []);
    const [dirty, setDirty] = useState(false);
    const id = useId();

    const canSave = canUpdateMedical(MedicalType.surgery) && !saving && option !== null;

    useEffect(() => {
        setDirty(
            (isDirty) =>
                isDirty ||
                option !== (surgery?.option_id ?? null) ||
                procedureDate !== (surgery?.procedure_date ?? null) ||
                description !== (surgery?.description ?? "") ||
                !elementsMatch(photographs, surgery?.photograph_ids ?? []),
        );
    }, [surgery, option, description, procedureDate, photographs]);

    const doSave = () => {
        if (!canSave) {
            return;
        }

        setSaving(id);
    };

    const commit = () => {
        storeMedical({
            type: MedicalType.surgery,
            id: surgery?.id,
            option_id: option,
            photograph_ids: photographs,
            procedure_date: procedureDate,
            description,
        })
            .then((id) => {
                id &&
                    setTimeout(
                        () => router.dismissTo({ pathname: `${SURGERY_BASE_URL}/[id]/show`, params: { id } }),
                        10,
                    );
            })
            .finally(() => setSaving(false));
    };

    const is_saving = saving === id;

    // biome-ignore lint/correctness/useExhaustiveDependencies(commit): callback
    useEffect(() => {
        is_saving && commit();
    }, [is_saving]);

    return (
        <FullPageModal
            {...modalProps}
            dirty={dirty}
            title={`${surgery?.id ? "Edit" : "Add"} procedure`}
            cancelable
            headerRight={() => (
                <Button disabled={!canSave} title="Save" size="sm" onPress={doSave} loading={is_saving} />
            )}
            onRequestClose={onRequestClose}
            gap={Space.lg}
        >
            <FieldGroup>
                <OptionSelector
                    type={OptionType.surgeries}
                    value={option}
                    onItemSelected={setOption}
                    label="Procedure name"
                    disabled={is_saving}
                    name="option_id"
                />
                <DatePicker
                    value={procedureDate}
                    onDateSelected={setProcedureDate}
                    label="Date of procedure"
                    placeholder="Select date"
                    disabled={is_saving}
                    name="procedure_date"
                />
            </FieldGroup>
            <FieldGroup>
                <TextArea
                    value={description}
                    onChangeText={setDescription}
                    label="Description"
                    optional
                    disabled={is_saving}
                    name="description"
                />
                <MediaPicker value={photographs} onSelectionChanged={setPhotographs} disabled={is_saving} />
            </FieldGroup>
        </FullPageModal>
    );
};
